import React, { useEffect, useMemo, useState } from "react";
import { CModal, CModalBody } from "@coreui/react";
import { useDispatch } from "react-redux";
import crossButton from "../../../components/assets/image/crossButton.svg";
import placeholderImage from "../../../components/assets/image/placeholder.png";
import "../../../components/common/styles/createAdmin.css";
import { hideAddDealerModal, showAddDealerModal } from "../../../redux/actionDispatcher/admin/dealerModalDispatcher";
import { addDealerRequest } from "../../../redux/actionDispatcher/admin/dealerListingDispatcher";
import "./AddDealer.css";
import CountryCodeDropdown from "../../common/countryCodeDropdown";
import { imageBaseUrls } from "../../../constant/ApiUrls";
import AddressInput from "./AddressInput";
import { fetchData } from "../../../services/api";
import { ApiURLs } from "../../../constant/ApiUrls";
//  import { addDealerRequest } from "../redux/dealerDispatcher"; // 🔹 create dispatcher similar to category
const DAYS = [
  "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"
];

const DEFAULT_HOURS = () =>
  DAYS.map((day) => ({ day, open: "09:00", close: "18:00", closed: false }));

// Kept in step with SEGMENT_ORDER in the API's utils/motorSegments.js -- these
// values are what get stored on users.motor_segments, so they are not free text.
// The values are what goes in the database and must not change; only the
// labels are for reading. Kept in step with SEGMENT_ORDER on the API.
const MOTOR_SEGMENTS = [
  { value: "cars", label: "Cars" },
  { value: "commercials", label: "Vans, Trucks & Commercials" },
  { value: "bikes", label: "Motorbikes & Scooters" },
  { value: "campers", label: "Campers & Caravans" },
  { value: "farm", label: "Farm Machinery" },
  { value: "plant", label: "Plant Machinery" },
];

/**
 * Seed the opening-hours rows from whatever the dealer already has stored.
 *
 * Every other field on this form is seeded from `dealerData`; opening hours
 * never were, so the form always opened on 09:00-18:00 no matter what was in
 * the database. Save it, reload, and you are looking at 09:00-18:00 again --
 * indistinguishable from the save having failed, which is how this was
 * reported.
 *
 * The stored row uses day_of_week / open_time / close_time / is_closed, and the
 * times come back as "09:00:00" while the <input type="time"> wants "09:00".
 */
const seedOpeningHours = (stored) => {
  let rows = stored;
  // The API aggregates these with JSON_ARRAYAGG. Depending on the driver that
  // arrives already parsed or as a string; accept either rather than trust one.
  if (typeof rows === "string") {
    try {
      rows = JSON.parse(rows);
    } catch (e) {
      rows = null;
    }
  }
  if (!Array.isArray(rows) || rows.length === 0) return DEFAULT_HOURS();

  const byDay = {};
  rows.forEach((r) => {
    if (r && (r.day_of_week || r.day)) byDay[String(r.day_of_week || r.day).trim()] = r;
  });

  const hhmm = (value, fallback) => {
    const text = String(value == null ? "" : value).trim();
    const m = text.match(/^(\d{1,2}):(\d{2})/);
    return m ? `${m[1].padStart(2, "0")}:${m[2]}` : fallback;
  };

  return DAYS.map((day) => {
    const row = byDay[day];
    if (!row) return { day, open: "09:00", close: "18:00", closed: false };
    return {
      day,
      open: hhmm(row.open_time != null ? row.open_time : row.open, "09:00"),
      close: hhmm(row.close_time != null ? row.close_time : row.close, "18:00"),
      closed: row.is_closed === 1 || row.is_closed === "1" || row.closed === true,
    };
  });
};

const locations = [
  {
    "id": 145,
    "name": "Antrim"
  },
  {
    "id": 146,
    "name": "Armagh"
  },
  {
    "id": 136,
    "name": "Carlow"
  },
  {
    "id": 143,
    "name": "Cavan"
  },
  {
    "id": 126,
    "name": "Clare"
  },
  {
    "id": 120,
    "name": "Cork"
  },
  {
    "id": 149,
    "name": "Derry"
  },
  {
    "id": 142,
    "name": "Donegal"
  },
  {
    "id": 147,
    "name": "Down"
  },
  {
    "id": 119,
    "name": "Dublin"
  },
  {
    "id": 148,
    "name": "Fermanagh"
  },
  {
    "id": 121,
    "name": "Galway"
  },
  {
    "id": 125,
    "name": "Kerry"
  },
  {
    "id": 129,
    "name": "Kildare"
  },
  {
    "id": 135,
    "name": "Kilkenny"
  },
  {
    "id": 134,
    "name": "Laois"
  },
  {
    "id": 141,
    "name": "Leitrim"
  },
  {
    "id": 122,
    "name": "Limerick"
  },
  {
    "id": 137,
    "name": "Longford"
  },
  {
    "id": 131,
    "name": "Louth"
  },
  {
    "id": 140,
    "name": "Mayo"
  },
  {
    "id": 130,
    "name": "Meath"
  },
  {
    "id": 144,
    "name": "Monaghan"
  },
  {
    "id": 133,
    "name": "Offaly"
  },
  {
    "id": 138,
    "name": "Roscommon"
  },
  {
    "id": 139,
    "name": "Sligo"
  },
  {
    "id": 123,
    "name": "Tipperary"
  },
  {
    "id": 150,
    "name": "Tyrone"
  },
  {
    "id": 124,
    "name": "Waterford"
  },
  {
    "id": 132,
    "name": "Westmeath"
  },
  {
    "id": 127,
    "name": "Wexford"
  },
  {
    "id": 128,
    "name": "Wicklow"
  }
];


const AddDealer = ({ editMode = false, dealerData = {}, tierList = [], }) => {
  const dispatch = useDispatch();
  const [isModalVisible, setIsModalVisible] = useState(false);



  // Dealer fields
  const [businessName, setBusinessName] = useState(dealerData?.business_name ?? "");
  const [email, setEmail] = useState(dealerData?.email ?? "");
  const [phone, setPhone] = useState(dealerData?.number ?? "");
  const [website, setWebsite] = useState(dealerData?.website ?? "");

  /* A link for the Go to website button.
     Dealers are typed in as "meskellmotorcycles.com" as often as with the
     https:// on the front, and a href with no scheme is read as a path on the
     admin panel itself - the button would quietly reload the superadmin. Any
     scheme other than http/https is refused rather than guessed at: nothing
     here should be able to turn a typed field into a javascript: link.
     Returns "" when there is nothing sensible to open, which is what disables
     the button. */
  const websiteHref = (value) => {
    const raw = String(value || "").trim();
    if (!raw) return "";
    if (/^https?:\/\//i.test(raw)) return raw;
    if (/^[a-z][a-z0-9+.-]*:/i.test(raw)) return "";
    if (!/^[^\s/]+\.[^\s/]/.test(raw)) return "";
    return `https://${raw}`;
  };
  const [name, setName] = useState(dealerData?.name ?? "");
  const [country, setCountry] = useState(dealerData?.country || "Ireland");
  const [locationId, setLocationId] = useState(dealerData?.location_id ?? "");
  const [location, setLocation] = useState(dealerData?.location ?? "");

  const [address, setAddress] = useState(dealerData?.business_address ?? "");
  const [suggestions, setSuggestions] = useState([]);
  const [placeId, setPlaceId] = useState(dealerData?.review_id ?? "");
  // Set when the user tells the address box that none of Google's suggestions
  // is the right one. Google does not have every Irish townland, and before
  // this the form simply refused to save such an address.
  const [addressManual, setAddressManual] = useState(false);


  const [about, setAbout] = useState(dealerData?.about ?? "");
  const [countryCode, setCountryCode] = useState(null);
  const [primarySector, setPrimarySector] = useState(
    dealerData?.primary_sector ? dealerData.primary_sector.split(",") : []
  );


  const [emailContact, setEmailContact] = useState(dealerData?.email_contact ?? "");
  const [phoneContact, setPhoneContact] = useState(dealerData?.phone_contact ?? "");
  // The API sends `whatsapp_contact` (see getDealers in dealerController). This
  // read the misspelling `whatsaap_contact`, which is what the API ACCEPTS on the
  // way in but never returns on the way out -- so the box opened empty for every
  // dealer, including those with a number stored. Accept both, prefer the real one.
  const [whatsaapContact, setWhatsappContact] = useState(
    dealerData?.whatsapp_contact ?? dealerData?.whatsaap_contact ?? ""
  );
  // const [reviewId, setReviewId] = useState(dealerData?.review_id ?? "");
  const [dealerStatus, setDealerStatus] = useState(dealerData?.dealer_status ?? 1);

  // Which marques this dealer holds a franchise for. Stored on users.make as a
  // comma-separated list; the API has always accepted it, there was simply no
  // way to set it from this form.
  const [makes, setMakes] = useState(
    dealerData?.make ? String(dealerData.make).split(",").map((m) => m.trim()).filter(Boolean) : []
  );
  const [makeOptions, setMakeOptions] = useState([]);

  // Which parts of the motor trade this dealer sells in. A Fuso franchise is a
  // commercial dealer, not a car dealer, and the form had no way to say so.
  const [motorSegments, setMotorSegments] = useState(
    dealerData?.motor_segments
      ? String(dealerData.motor_segments).split(",").map((s) => s.trim()).filter(Boolean)
      : []
  );
  const [makeSearch, setMakeSearch] = useState("");


  const [selectedTier, setSelectedTier] = useState(dealerData?.new_tier_id ?? null);

  // Profile image
  const [profileImage, setProfileImage] = useState(dealerData?.image != null ? imageBaseUrls(dealerData?.image) : null);
  const [uploadProfileImage, setUploadProfileImage] = useState(null);



  // Background image
  const [backgroundImage, setBackgroundImage] = useState(dealerData?.bg_image != null ? imageBaseUrls(dealerData?.bg_image) : null);
  const [uploadBackgroundImage, setUploadBackgroundImage] = useState(null);



  // Logo image
  const [logoImage, setLogoImage] = useState(dealerData?.logo != null ? imageBaseUrls(dealerData?.logo) : null);
  const [uploadLogoImage, setUploadLogoImage] = useState(null);

  // Errors
  const [errors, setErrors] = useState({});



  const [loading, setLoading] = useState(false);


  // Opening hours
  const [openingHours, setOpeningHours] = useState(() =>
    seedOpeningHours(dealerData?.opening_hours)
  );



  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchData(ApiURLs.DEALER_MAKES);
        if (!cancelled && res?.status === 1 && Array.isArray(res.data)) {
          setMakeOptions(res.data);
        }
      } catch (e) {
        // A missing list must not block the rest of the form; the field just
        // renders empty and everything else still saves.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const toggleMake = (make) => {
    setMakes((prev) =>
      prev.includes(make) ? prev.filter((m) => m !== make) : [...prev, make]
    );
  };

  const toggleSegment = (segment) => {
    setMotorSegments((prev) =>
      prev.includes(segment) ? prev.filter((s) => s !== segment) : [...prev, segment]
    );
  };

  /**
   * The marques offered in the franchise list.
   *
   * The full catalogue is 1,356 marques, so it is narrowed by whatever the
   * dealer sells and by the search box. Two rules that matter:
   *   - a marque that is already ticked is ALWAYS shown, whatever the filter
   *     says, otherwise narrowing the list hides a selection and there is no
   *     way to untick it;
   *   - searching ignores the segment filter entirely, so nothing in the
   *     catalogue can ever become unreachable through this form. The filter is
   *     there to shorten the list, not to decide what is allowed.
   */
  const visibleMakes = useMemo(() => {
    const term = makeSearch.trim().toLowerCase();
    if (term) {
      return makeOptions.filter((o) => String(o.make).toLowerCase().includes(term));
    }
    if (!motorSegments.length) return makeOptions;
    return makeOptions.filter(
      (o) =>
        makes.includes(o.make) ||
        (o.segments || []).some((s) => motorSegments.includes(s))
    );
  }, [makeOptions, makeSearch, motorSegments, makes]);

  /* Load the dealer's saved record into the form.
   *
   * Every field above is initialised with `useState(dealerData?.something)`, and
   * a useState initialiser only ever runs on the FIRST render of a component
   * instance. This form is rendered inside a cell of the dealer table, and that
   * table reuses its cell components as the rows underneath them change - after
   * a save, a refetch, a sort, a page change or a scroll. So the component stays
   * mounted, `dealerData` quietly becomes a different dealer, and the state
   * keeps whatever it was given the first time.
   *
   * That is why details and images vanished on revisiting an account: the form
   * was showing stale state rather than the record, and saving then wrote that
   * stale state back over the real data.
   *
   * Calling this every time the modal is opened means the form always shows what
   * is actually stored right now, for the dealer whose button was pressed.
   */
  const populateFromDealer = (d = {}) => {
    setBusinessName(d?.business_name ?? "");
    setEmail(d?.email ?? "");
    setPhone(d?.number ?? "");
    setWebsite(d?.website ?? "");
    setName(d?.name ?? "");
    setCountry(d?.country || "Ireland");
    setLocationId(d?.location_id ?? "");
    setLocation(d?.location ?? "");
    setAddress(d?.business_address ?? "");
    setSuggestions([]);
    setPlaceId(d?.review_id ?? "");
    setAddressManual(false);
    setCountryCode(null);
    setAbout(d?.about ?? "");
    setPrimarySector(d?.primary_sector ? String(d.primary_sector).split(",") : []);
    setEmailContact(d?.email_contact ?? "");
    setPhoneContact(d?.phone_contact ?? "");
    setWhatsappContact(d?.whatsapp_contact ?? d?.whatsaap_contact ?? "");
    setDealerStatus(d?.dealer_status ?? 1);
    setMakes(
      d?.make ? String(d.make).split(",").map((m) => m.trim()).filter(Boolean) : []
    );
    setMotorSegments(
      d?.motor_segments
        ? String(d.motor_segments).split(",").map((s) => s.trim()).filter(Boolean)
        : []
    );
    setMakeSearch("");
    setSelectedTier(d?.new_tier_id ?? null);
    // The three images are shown from the stored path. Any file picked in a
    // previous session of this form is cleared, otherwise an upload chosen for
    // one dealer could be saved onto the next one opened.
    setProfileImage(d?.image != null ? imageBaseUrls(d?.image) : null);
    setUploadProfileImage(null);
    setBackgroundImage(d?.bg_image != null ? imageBaseUrls(d?.bg_image) : null);
    setUploadBackgroundImage(null);
    setLogoImage(d?.logo != null ? imageBaseUrls(d?.logo) : null);
    setUploadLogoImage(null);
    setOpeningHours(seedOpeningHours(d?.opening_hours));
    setErrors({});
    setLoading(false);
  };

  const resetForm = () => {
    setBusinessName("");
    setEmail("");
    setPhone("");
    setWebsite("");
    setPrimarySector([]);
    setName("");
    setCountry("Ireland"); // keep default
    setCountryCode(null);      // reset dropdown too
    setLocation("");
    setLocationId("");
    setAddress("");
    setSuggestions([]);
    setPlaceId("");
    setAddressManual(false);
    setAbout("");
    setProfileImage(null);
    setUploadProfileImage(null);
    setUploadLogoImage(null);
    setLogoImage(null);
    setUploadBackgroundImage(null);
    setBackgroundImage(null);
    setErrors({});
    setEmailContact("");
    setPhoneContact("");
    setWhatsappContact("");
    // setReviewId("");
    setDealerStatus(1);
    setMakes([]);
    setMotorSegments([]);
    setMakeSearch("");
    setLoading(false);
    setOpeningHours(DEFAULT_HOURS());
  };

  const handleHoursChange = (index, key, value) => {
    const updated = [...openingHours];
    updated[index][key] = value;
    setOpeningHours(updated);
  };

  const handleProfileImageChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const validTypes = ["image/jpeg", "image/png", "image/jpg"];
    if (!validTypes.includes(file.type)) {
      alert("Please select a valid image file (JPEG, PNG, JPG)");
      return;
    }
    const imageUrl = URL.createObjectURL(file);
    setProfileImage(imageUrl);
    setUploadProfileImage(file);
  };



  const handleBackgroundImageChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const validTypes = ["image/jpeg", "image/png", "image/jpg"];
    if (!validTypes.includes(file.type)) {
      alert("Please select a valid image file (JPEG, PNG, JPG)");
      return;
    }
    const imageUrl = URL.createObjectURL(file);
    setBackgroundImage(imageUrl);
    setUploadBackgroundImage(file);
  };


  const handleLogoImageChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const validTypes = ["image/jpeg", "image/png", "image/jpg"];
    if (!validTypes.includes(file.type)) {
      alert("Please select a valid image file (JPEG, PNG, JPG)");
      return;
    }
    const imageUrl = URL.createObjectURL(file);
    setLogoImage(imageUrl);
    setUploadLogoImage(file);
  };




  const capitalizeWords = (value) => {
    return value
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };




  /*
    What must be filled in before this form will save.

    This list is the reason opening hours "would not save". Every field below was
    required, and a single miss made handleSubmit return without a word -- no
    message, no highlight near the button, nothing. The error text renders inline
    beside its own field, which on this form can be several screens above the Save
    button. Fill in the hours at the bottom, press Save, and the page sits there.

    Two changes.

    First, the WhatsApp number is optional now, as the client asked. It was worse
    than merely strict: the field is seeded from `whatsapp_contact`, and the code
    read `whatsaap_contact` -- a spelling that appears nowhere in the API response.
    So the box opened empty for EVERY dealer, including the ones with a number
    stored, and then refused to save because it was empty. Nobody could get past it
    without typing a number in by hand.

    Second, the rest of the profile block -- tier, about, and the three contact
    preferences -- no longer blocks a save either. None of them is structural;
    they are things a dealer profile may or may not have filled in yet, and 63 of
    the 91 dealers arrived from an importer without them. Requiring them meant the
    only dealers whose hours could be edited were the ones that needed it least.

    What is still required is the dealer's identity: who they are, where they are,
    how to reach them. Those genuinely should not be blanked by accident.
  */
  const validate = () => {
    let newErrors = {};
    if (!businessName.trim()) newErrors.businessName = "Business name is required";
    if (!email.trim()) newErrors.email = "Email is required";
    if (!phone.trim()) newErrors.phone = "Phone number is required";
    if (!website.trim()) newErrors.website = "Website is required";
    if (!name.trim()) newErrors.name = "Contact person name is required";
    if (!country.trim()) newErrors.country = "Country is required";
    if (!countryCode) newErrors.countryCode = "Country code is required";
    if (!locationId) newErrors.locationId = "Location is required";
    if (!address.trim()) {
      newErrors.address = "Address is required";
    }
    // Only demand a fresh pick from the suggestions when the address text has
    // actually been edited. Otherwise re-saving a dealer whose stored address
    // predates the place-id lookup forced you to retype an address that was
    // already correct, just to change something else on the page.
    else if (!placeId && !addressManual && address.trim() !== (dealerData?.business_address ?? "").trim()) {
      newErrors.address = "Please select address from suggestions";
    }

    setErrors(newErrors);
    // Hand the caller the actual problems, not just a yes/no. A refused save has
    // to be able to say WHICH field refused it.
    return newErrors;
  };

  const FIELD_LABELS = {
    businessName: "Business name", email: "Email", phone: "Phone number",
    website: "Website", name: "Contact person name", country: "Country",
    countryCode: "Country code", locationId: "Location", address: "Address",
  };
  const [submitError, setSubmitError] = useState("");

  const handleSubmit = async () => {
    const problems = validate();
    if (Object.keys(problems).length) {
      // Never fail silently. Name what is missing, right beside the button that
      // was just pressed, and take the page to the first offending field.
      const missing = Object.keys(FIELD_LABELS).filter((f) => problems[f]);
      setSubmitError(
        missing.length
          ? `Not saved. Still needed: ${missing.map((f) => FIELD_LABELS[f]).join(", ")}.`
          : "Not saved. Please check the fields marked in red above."
      );
      const el = document.querySelector(".text-danger");
      if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setSubmitError("");
    const data = {
      business_name: businessName,       // DB: business_name
      flag: countryCode.v.replace('+', ''), // DB: flag
      country_code: countryCode.cc,      // DB: country_code
      number: phone,                     // DB: number
      email,                             // DB: email
      website,                           // DB: (no field → new table/ignore OR add to business_address text)
      name,                              // DB: name
      country,                           // DB: country
      location_id: locationId,           // DB: location_id
      location,                          // DB: location
      address,                           // DB: business_address
      about,                             // DB: (no direct column → need dealer_details table or use business_address/extra column)
      opening_hours: openingHours,       // DB: separate table opening_hours (FK = user_id)
      image: uploadProfileImage, // DB: image

      vendor_type: "dealer",             // DB: vendor_type   // DB: password
      required_change_password: 1,       // DB: (⚠️ missing in your table → you need to add column tinyint default 0)
      primary_sector: primarySector.join(","), // 🔹 added
      tier_id: selectedTier,
      // A hand-typed address has no Google place behind it. Keep whatever
      // place we already held rather than blanking it - the business is the
      // same business, and that id is what their Google reviews hang off.
      review_id: placeId || (addressManual ? (dealerData?.review_id ?? "") : ""),
      whatsaap_contact: whatsaapContact,
      email_contact: emailContact,
      phone_contact: phoneContact,
      dealer_status: dealerStatus,
      // Only meaningful for a main dealer. Cleared otherwise so an independent
      // that used to be a franchise does not keep a stale badge.
      make: dealerStatus === 1 ? makes.join(",") : "",
      // Applies to independents as much as to franchises -- it describes what
      // the dealer sells, not who they hold a franchise from.
      motor_segments: primarySector.includes("Motors") ? motorSegments.join(",") : "",
    };

    if (uploadBackgroundImage) {
      data.bg_image = uploadBackgroundImage;
    }


    if (uploadLogoImage) {
      data.logo = uploadLogoImage
    }

    if (editMode) {
      data.id = dealerData.id;
    }

    console.log("Payload 👉", data);


    setLoading(true);

    const result = await dispatch(addDealerRequest(data));
    setLoading(false);

    if (result) {
      if (!editMode) resetForm();
      setIsModalVisible(false);
      dispatch(hideAddDealerModal());
    }

  };

  const handleClose = () => {
    setIsModalVisible(false);
    dispatch(hideAddDealerModal());
  };

  return (
    <div>
      {/* Open Button */}
      <button
        onClick={() => {
          // Add: start from a clean sheet. Edit: load what is stored for THIS
          // dealer, right now. Doing it on every open is what stops the form
          // showing a previous dealer's details, or nothing at all, when the
          // table underneath has reused this component for a different row.
          if (editMode) populateFromDealer(dealerData);
          else resetForm();
          setIsModalVisible(true);
          dispatch(showAddDealerModal());
        }}
        style={{
          backgroundColor: editMode ? "#1b87f4" : "rgb(0 0 0)",
          color: "white",
          border: "none",
          cursor: "pointer",
          borderRadius: "4px",
          fontSize: editMode ? "10px" : "14px",
          minWidth: "60px",
          height: editMode ? "24px" : "30px",
          padding: editMode ? "2px 5px" : "4px 10px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {editMode ? "Edit Dealer" : "Add Dealer"}
      </button>

      {/* Modal */}
      <CModal
        alignment="center"
        visible={isModalVisible}
        onClose={handleClose}
        className="attributemodal"
      >
        <CModalBody className="main-container createAdmin">
          <div style={{ maxWidth: 920, padding: "0 20px" }}>
            <div className="cross-btn-div d-flex" style={{ justifyContent: "space-between", minWidth: "100%" }}>
              <h4
                className="h3 fw-bold mb-3 mt-2"
                style={{ color: "#007bff", textAlign: "center" }}
              >
                {editMode ? "Edit Dealer" : "Add New Dealer"}
              </h4>
              <img
                onClick={handleClose}
                src={crossButton}
                alt="close"
                className="cross-button"
                style={{ cursor: "pointer" }}
              />
            </div>

            <form onSubmit={(e) => e.preventDefault()}>
              {/* Profile image */}
              <div style={{ display: "flex", gap: 16, alignItems: "flex-start", marginBottom: 12 }}>
                <div style={{ minWidth: 140, textAlign: "center" }}>
                  <div
                    style={{
                      marginBottom: 10,
                      marginTop: 0,
                      fontSize: "14px",
                      fontWeight: 500
                    }}
                  >
                    Profile Image
                  </div>

                  <input
                    type="file"
                    accept="image/*"
                    id="profileImageUpload"
                    style={{ display: "none" }}
                    onChange={handleProfileImageChange}
                  />

                  <img
                    onClick={() =>
                      document.getElementById("profileImageUpload").click()
                    }
                    src={profileImage || placeholderImage}
                    alt="Profile Preview"
                    style={{
                      height: 120,
                      width: 120,
                      borderRadius: 8,
                      objectFit: "cover",
                      cursor: "pointer",
                      border: "1px solid #ddd"
                    }}
                    title="Click to upload profile image"
                  />

                  {errors.profileImage && (
                    <div
                      className="text-danger mt-1"
                      style={{ fontSize: "12px" }}
                    >
                      {errors.profileImage}
                    </div>
                  )}
                </div>

                <div style={{ flex: 1 }}>
                  <input
                    className="form-control mt-2"
                    placeholder="Business Name"
                    value={businessName}
                    autofill="off"
                    autoComplete="new-password"   // ✅ best trick for Chrome

                    onChange={async (e) => {
                      await setBusinessName(capitalizeWords(e.target.value));
                      validate();

                    }} />
                  {errors.businessName && <div className="text-danger" style={{ fontSize: "12px" }}>{errors.businessName}</div>}

                  <div style={{ display: "flex", gap: 8 }}>
                    <div style={{ flex: 1 }}>
                      <input
                        className="form-control mt-2"
                        placeholder="Email"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value)
                          validate();

                        }}
                        type="email"
                        autofill="off"
                        autoComplete="new-password"   // ✅ best trick for Chrome

                      />
                      {errors.email && <div className="text-danger" style={{ fontSize: "12px" }}>{errors.email}</div>}
                    </div>

                    <CountryCodeDropdown className="mt-2" height={38} onChange={setCountryCode} value={countryCode != null ? countryCode.cc : dealerData?.country_code ?? null} />
                    <div>
                      <input
                        type="tel"
                        className="form-control mt-2"
                        placeholder="Phone"
                        value={phone}
                        autofill="off"
                        autoComplete="new-password"   // ✅ best trick for Chrome
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, ""); // remove non-digits
                          setPhone(val);
                          validate();
                        }}
                        style={{ width: 200 }}
                      />
                      {errors.phone && <div className="text-danger" style={{ fontSize: "12px" }}>{errors.phone}</div>}
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: 8 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        <input
                          className="form-control mt-2"
                          placeholder="Dealer Website"
                          value={website}
                          onChange={(e) => {
                            setWebsite(e.target.value);
                            validate();

                          }}
                          autofill="off"
                          autoComplete="new-password"   // ✅ best trick for Chrome

                        />
                        {/* Opens whatever is typed in the box, not whatever was
                            saved, so the address can be checked before the
                            dealer is created. A new tab: this form holds
                            unsaved work and navigating away would lose it. */}
                        <a
                          className="btn btn-outline-secondary mt-2"
                          href={websiteHref(website)}
                          target="_blank"
                          rel="noreferrer noopener"
                          title={website.trim() ? "Open this website in a new tab" : "Enter a website address first"}
                          onClick={(e) => { if (!websiteHref(website)) e.preventDefault(); }}
                          style={{
                            whiteSpace: "nowrap",
                            pointerEvents: websiteHref(website) ? "auto" : "none",
                            opacity: websiteHref(website) ? 1 : 0.5,
                          }}
                        >
                          Go to website
                        </a>
                      </div>
                      {errors.website && <div className="text-danger" style={{ fontSize: "12px" }}>{errors.website}</div>}
                    </div>
                    <div>
                      <input
                        className="form-control mt-2"
                        placeholder="Contact Person Name"
                        value={name}
                        onChange={(e) => {




                          setName(capitalizeWords(e.target.value));
                          validate();
                        }}
                        autofill="off"
                        autoComplete="new-password"   // ✅ best trick for Chrome

                        style={{ width: 220 }}
                      />
                      {errors.name && <div className="text-danger" style={{ fontSize: "12px" }}>{errors.name}</div>}
                    </div>
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 16, alignItems: "flex-start", marginBottom: 12 }}>
                <div style={{ minWidth: 140, textAlign: "center" }}>
                  <div
                    style={{
                      marginBottom: 10,
                      marginTop: 0,
                      fontSize: "14px",
                      fontWeight: 500
                    }}
                  >
                    Background Image
                  </div>

                  <input
                    type="file"
                    accept="image/*"
                    id="backgroundImageUpload"
                    style={{ display: "none" }}
                    onChange={handleBackgroundImageChange}
                  />

                  <img
                    onClick={() =>
                      document.getElementById("backgroundImageUpload").click()
                    }
                    src={backgroundImage || placeholderImage}
                    alt="Background Preview"
                    style={{
                      height: 120,
                      width: 120,
                      borderRadius: 8,
                      objectFit: "cover",
                      cursor: "pointer",
                      border: "1px solid #ddd"
                    }}
                    title="Click to upload background image"
                  />

                  {errors.backgroundImage && (
                    <div
                      className="text-danger mt-1"
                      style={{ fontSize: "12px" }}
                    >
                      {errors.backgroundImage}
                    </div>
                  )}
                </div>

                <div style={{ flex: 1 }}>
                  {/* Primary Sector */}
                  <div className="mt-3 mb-3">  {/* added mb-3 for bottom spacing */}
                    <label className="form-label fw-bold">Primary Sectors</label>
                    {/*
                      This list has to cover every value the public site can act on,
                      because Find a Dealer filters strictly on the sector: a business
                      whose sector is blank appears on no directory page at all.

                      Two were missing. "Farming" already drives /find-a-dealer/Farming
                      but could not be picked here, so opening a machinery dealer showed
                      no selection and saving would quietly wipe it. "Marketplace" had
                      nowhere to go, which is why the six supplier shops - A Room
                      Outside, Coola Living, Fix46, 3D Qube, Xcellent PC, National
                      Packaging - were sitting outside the directory with nearly 2,900
                      live adverts between them.
                    */}
                    <div style={{ display: "flex", gap: "16px", marginTop: 4, flexWrap: "wrap" }}>
                      {["Motors", "Property", "Farming", "Marketplace", "Jobs"].map((sector) => (
                        <label key={sector} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                          <input
                            type="radio"
                            name="primary_sector"
                            value={sector}
                            checked={primarySector.includes(sector)}
                            onChange={() => {
                              // A business sits in one sector: pick one, drop the rest.
                              setPrimarySector([sector]);
                            }}
                          />
                          {sector}
                        </label>
                      ))}
                    </div>
                  </div>

                  {primarySector.includes("Motors") && (
                  <div className="mt-3 mb-3">
                    <label className="form-label fw-bold">Dealer Type</label>

                    <div style={{ display: "flex", gap: "18px", marginTop: 6 }}>
                      {[
                        { label: "Main Dealer", value: 1 },
                        { label: "Independent Dealer", value: 2 }
                      ].map((item) => (
                        <label
                          key={item.value}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            fontSize: "14px",
                            cursor: "pointer"
                          }}
                        >
                          <input
                            type="radio"
                            name="dealer_status"
                            value={item.value}
                            checked={dealerStatus === item.value}
                            onChange={() => setDealerStatus(item.value)}
                            style={{
                              width: "14px",
                              height: "14px",
                              margin: 0,
                              accentColor: "#0d6efd" // optional (bootstrap blue)
                            }}
                          />
                          {item.label}
                        </label>
                      ))}
                    </div>

                    {/* What the dealer actually sells. A Fuso or Scania
                        franchise is a commercial dealer; a Ducati franchise is
                        a bike dealer. Without this the franchise list below can
                        only be read as a list of car marques. */}
                    <div className="mt-3">
                      <label className="form-label fw-bold">
                        Sells{" "}
                        <span style={{ fontWeight: 400, color: "#6c757d", fontSize: "13px" }}>
                          (tick everything this dealer handles)
                        </span>
                      </label>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "18px", marginTop: 6 }}>
                        {MOTOR_SEGMENTS.map((seg) => (
                          <label
                            key={seg.value}
                            style={{
                              display: "flex", alignItems: "center", gap: "6px",
                              fontSize: "14px", cursor: "pointer",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={motorSegments.includes(seg.value)}
                              onChange={() => toggleSegment(seg.value)}
                              style={{ width: "14px", height: "14px", margin: 0, accentColor: "#0d6efd" }}
                            />
                            {seg.label}
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* A main dealer holds a franchise for one or more marques.
                        This is what drives the "All Makes" filter on Find a
                        Dealer, so without it that filter has nothing to work
                        with. Hidden for independents, where it has no meaning. */}
                    {dealerStatus === 1 && (
                      <div className="mt-3">
                        <label className="form-label fw-bold">
                          Franchise for{" "}
                          <span style={{ fontWeight: 400, color: "#6c757d", fontSize: "13px" }}>
                            (tick every marque this dealer is a main dealer for)
                          </span>
                        </label>

                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search all manufacturers, e.g. Fuso, Scania, Ducati"
                          value={makeSearch}
                          onChange={(e) => setMakeSearch(e.target.value)}
                          style={{ marginBottom: "8px", fontSize: "14px" }}
                        />

                        {makes.length > 0 && (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "8px" }}>
                            {makes.map((m) => (
                              <span
                                key={m}
                                onClick={() => toggleMake(m)}
                                title="Remove"
                                style={{
                                  background: "#0d6efd", color: "#fff", borderRadius: "14px",
                                  padding: "3px 10px", fontSize: "13px", cursor: "pointer",
                                }}
                              >
                                {m} &times;
                              </span>
                            ))}
                          </div>
                        )}

                        <div
                          style={{
                            maxHeight: "170px", overflowY: "auto", border: "1px solid #dee2e6",
                            borderRadius: "6px", padding: "8px", display: "flex",
                            flexWrap: "wrap", gap: "4px 14px",
                          }}
                        >
                          {makeOptions.length === 0 && (
                            <span style={{ fontSize: "13px", color: "#6c757d" }}>
                              Loading manufacturers&hellip;
                            </span>
                          )}
                          {makeOptions.length > 0 && visibleMakes.length === 0 && (
                            <span style={{ fontSize: "13px", color: "#6c757d" }}>
                              No manufacturer matches that.
                            </span>
                          )}
                          {visibleMakes.map((opt) => (
                            <label
                              key={opt.make}
                              style={{
                                display: "flex", alignItems: "center", gap: "6px",
                                fontSize: "14px", cursor: "pointer", minWidth: "150px",
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={makes.includes(opt.make)}
                                onChange={() => toggleMake(opt.make)}
                                style={{ width: "14px", height: "14px", margin: 0, accentColor: "#0d6efd" }}
                              />
                              {opt.make}
                              {/* The number is how many adverts the site already
                                  carries for that marque. A marque with none --
                                  every truck and most bike marques -- shows no
                                  number rather than "(0)", which reads as a
                                  problem rather than as "nobody has listed one
                                  yet". */}
                              {opt.ads > 0 && (
                                <span style={{ color: "#adb5bd", fontSize: "12px" }}>({opt.ads})</span>
                              )}
                            </label>
                          ))}
                        </div>

                        <div style={{ fontSize: "12px", color: "#6c757d", marginTop: "6px" }}>
                          {makeSearch.trim()
                            ? `${visibleMakes.length} of ${makeOptions.length} manufacturers match "${makeSearch.trim()}".`
                            : motorSegments.length
                              ? `Showing ${visibleMakes.length} manufacturers for what this dealer sells. Search above to reach any of the ${makeOptions.length}.`
                              : `Showing all ${makeOptions.length} manufacturers. Tick what the dealer sells above to shorten this list.`}
                        </div>
                      </div>
                    )}
                  </div>
                  )}



                  {/* Country & Location */}
                  <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                    <div>
                      <select
                        className="form-control mt-2"
                        value={country}
                        onChange={(e) => {
                          setCountry(e.target.value);
                          validate();
                        }}
                        style={{ width: 190 }}
                      >
                        <option value="Ireland">Ireland</option>
                      </select>
                      {errors.country && <div className="text-danger" style={{ fontSize: "12px" }}>{errors.country}</div>}
                    </div>

                    <div>
                      <select
                        className="form-control mt-2"
                        value={locationId}
                        onChange={(e) => {
                          const id = e.target.value;
                          setLocationId(id);
                          const loc = locations.find((l) => l.id === Number(id));
                          setLocation(loc?.name || "");
                          validate();
                        }}
                        style={{ width: 190 }}
                      >
                        <option value="">Select Location</option>
                        {locations.map((loc) => (
                          <option key={loc.id} value={loc.id}>
                            {loc.name}
                          </option>
                        ))}
                      </select>
                      {errors.locationId && <div className="text-danger" style={{ fontSize: "12px" }}>{errors.locationId}</div>}
                    </div>

                    <div className="mb-3">



                      <select
                        className="form-control mt-2"
                        value={selectedTier}
                        onChange={(e) => setSelectedTier(e.target.value)}
                        style={{ width: 190 }}
                      >
                        <option value="">Select Tier</option>
                        {tierList.map((tier) => (
                          <option key={tier.id} value={tier.id}>
                            {tier.name}
                          </option>
                        ))}
                      </select>

                      {errors.selectedTier && <div className="text-danger" style={{ fontSize: "12px" }}>{errors.selectedTier}</div>}


                    </div>
                  </div>

                </div>
              </div>


              <div style={{ display: "flex", gap: 16, alignItems: "flex-start", marginBottom: 12 }}>
                <div style={{ minWidth: 140, textAlign: "center" }}>
                  <div
                    style={{
                      marginBottom: 10,
                      marginTop: 0,
                      fontSize: "14px",
                      fontWeight: 500
                    }}
                  >
                    Business Logo
                  </div>

                  <input
                    type="file"
                    accept="image/*"
                    id="logoImageUpload"
                    style={{ display: "none" }}
                    onChange={handleLogoImageChange}
                  />

                  <img
                    onClick={() =>
                      document.getElementById("logoImageUpload").click()
                    }
                    src={logoImage || placeholderImage}
                    alt="Logo Preview"
                    style={{
                      height: 120,
                      width: 120,
                      borderRadius: 8,
                      objectFit: "cover",
                      cursor: "pointer",
                      border: "1px solid #ddd"
                    }}
                    title="Click to upload logo image"
                  />

                  {errors.logoImage && (
                    <div
                      className="text-danger mt-1"
                      style={{ fontSize: "12px" }}
                    >
                      {errors.logoImage}
                    </div>
                  )}
                </div>


                <div style={{ flex: 1 }}>



                  <div>

                    <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                      <div>
                        <input
                          type="tel"
                          className="form-control mt-2"
                          placeholder="Contact Phone"
                          value={phoneContact}
                          autofill="off"
                          autoComplete="new-password"   // ✅ best trick for Chrome
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, ""); // remove non-digits
                            setPhoneContact(val);
                            validate();
                          }}
                          style={{ width: 200 }}
                        />
                        {errors.phoneContact && <div className="text-danger" style={{ fontSize: "12px" }}>{errors.phoneContact}</div>}
                      </div>

                      <div>
                        <input
                          type="email"
                          className="form-control mt-2"
                          placeholder="Contact Email"
                          value={emailContact}
                          autofill="off"
                          autoComplete="new-password"   // ✅ best trick for Chrome
                          onChange={(e) => {
                            const val = e.target.value; // remove non-digits
                            setEmailContact(val);
                            validate();
                          }}
                          style={{ width: 200 }}
                        />
                        {errors.emailContact && <div className="text-danger" style={{ fontSize: "12px" }}>{errors.emailContact}</div>}
                      </div>


                      <div>
                        <input
                          type="tel"
                          className="form-control mt-2"
                          placeholder="WhatsApp number (optional)"
                          value={whatsaapContact}
                          autofill="off"
                          autoComplete="new-password"   // ✅ best trick for Chrome
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, ""); // remove non-digits
                            setWhatsappContact(val);
                            validate();
                          }}
                          style={{ width: 200 }}
                        />
                        {errors.whatsaapContact && <div className="text-danger" style={{ fontSize: "12px" }}>{errors.whatsaapContact}</div>}
                      </div>

                    </div>



                  </div>


                  <div>

                    <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                      {/* Address */}

                      {/* <div>

                        <input
                          className="form-control mt-2"
                          placeholder="Address"
                          value={address}
                          onChange={(e) => {
                            setAddress(e.target.value);
                            validate();
                          }}
                          style={{ width: 410 }}

                          autofill="off"
                          autoComplete="new-password"   // ✅ best trick for Chrome

                        />
                        {errors.address && <div className="text-danger" style={{ fontSize: "12px" }}>{errors.address}</div>}

                      </div> */}

                      <AddressInput
                        address={address}
                        setAddress={setAddress}
                        onManualAddress={setAddressManual}
                        suggestions={suggestions}
                        setSuggestions={setSuggestions}
                        // placeId={placeId}
                        setPlaceId={setPlaceId}
                        errors={errors}
                      />

                      {/* <div>
                        <input
                          type="text"
                          className="form-control mt-2"
                          placeholder="Google Review Cusotmer Id"
                          value={reviewId}
                          autofill="off"
                          autoComplete="new-password"   // ✅ best trick for Chrome
                          onChange={(e) => {
                            const val = e.target.value; // remove non-digits
                            setReviewId(val);
                            validate();
                          }}
                          style={{ width: 200 }}
                        />
                        {errors.reviewId && <div className="text-danger" style={{ fontSize: "12px" }}>{errors.reviewId}</div>}
                      </div> */}




                    </div>



                  </div>


                  {/* About */}
                  <textarea
                    className="form-control mt-2"
                    placeholder="About Dealer"
                    rows="3"
                    value={about}
                    onChange={(e) => {
                      setAbout(e.target.value);
                      validate();
                    }}
                  />
                  {errors.about && <div className="text-danger" style={{ fontSize: "12px" }}>{errors.about}</div>}



                </div>
              </div>

              {/* Primary Sector */}







              {/* Opening Hours */}
              <h6 className="fw-bold mt-3 text-center">Opening Hours</h6>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",

                }}
              >
                <div style={{
                  display: "grid",
                  gridTemplateColumns: "120px 140px 16px 140px 100px",
                  gap: "8px 12px",
                  alignItems: "center",
                  marginBottom: 8,
                  fontSize: 13,
                  color: "#555"
                }}>
                  <div style={{ fontWeight: 600 }}>Day</div>
                  <div style={{ fontWeight: 600 }}>Opens</div>
                  <div></div>
                  <div style={{ fontWeight: 600 }}>Closes</div>
                  <div style={{ fontWeight: 600 }}>Closed</div>
                </div>

                {openingHours.map((item, index) => (
                  <div
                    key={item.day}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "120px 140px 16px 140px 100px",
                      gap: "8px 12px",
                      alignItems: "center",
                      marginBottom: 6,
                    }}
                  >
                    <div style={{ paddingRight: 4 }}>{item.day}</div>
                    <input
                      type="time"
                      value={item.open}
                      disabled={item.closed}
                      onChange={(e) => handleHoursChange(index, "open", e.target.value)}
                      className="form-control"
                      style={{ maxWidth: "140px" }}
                    />
                    <div style={{ textAlign: "center" }}>to</div>
                    <input
                      type="time"
                      value={item.close}
                      disabled={item.closed}
                      onChange={(e) => handleHoursChange(index, "close", e.target.value)}
                      className="form-control"
                      style={{ maxWidth: "140px" }}
                    />
                    <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <input
                        type="checkbox"
                        checked={item.closed}
                        onChange={(e) =>
                          handleHoursChange(index, "closed", e.target.checked)
                        }
                      />
                      <span style={{ fontSize: 13 }}>Closed</span>
                    </label>
                  </div>
                ))}

              </div>



              {/* The save was refused. Say so here, where the eye already is,
                  rather than only in red text beside a field several screens up. */}
              {submitError && (
                <div
                  className="text-center mt-3"
                  style={{
                    color: "#b42318",
                    background: "#fef3f2",
                    border: "1px solid #fecdca",
                    borderRadius: "8px",
                    padding: "10px 14px",
                    fontSize: "14px",
                  }}
                >
                  {submitError}
                </div>
              )}

              <div className="text-center">
                <button
                  className="btn btn-lg btn-primary fw-bold text-light mt-3"
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading}
                  style={{ minWidth: "180px" }}
                >
                  {loading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      Processing...
                    </>
                  ) : (
                    editMode ? "Update Dealer" : "Submit Dealer"
                  )}
                </button>
              </div>

              {/* <div className="text-center">
                <button
                  className="btn btn-lg btn-primary fw-bold text-light mt-3"
                  type="button"
                  onClick={handleSubmit}
                >
                  {editMode ? "Update Dealer" : "Submit Dealer"}
                </button>
              </div> */}
            </form>
          </div>
        </CModalBody>
      </CModal>
    </div>
  );
};

export default AddDealer;